import React, { useEffect, useRef } from "react";
import { Route, Routes, useLocation } from "react-router-dom";

import Navbar from "./components/Navbar";
import Home from "./pages/Home";
import Sueros from "./pages/Sueros";
import SueroDetalle from "./pages/SueroDetalle";
import Tratamientos from "./pages/Tratamientos";
import CelulasMadre from "./pages/CelulasMadre";
import TratamientoDetalle from "./pages/TratamientoDetalle";
import Nosotros from "./pages/Nosotros";
import Contacto from "./pages/Contacto";
import Reservar from "./pages/Reservar";
import Empresas from "./pages/Empresas";
import Footer from "./components/Footer";
import FloatingWhatsApp from "./components/FloatingWhatsApp";
import SeoManager from "./components/SeoManager";
import { normalizePath, scrollToPath, scrollToTop } from "./lib/sectionNavigation";

function MainLayout({ children }) {
  const location = useLocation();

  useEffect(() => {
    const root = document.documentElement;
    let frame = null;

    const onMouseMove = (event) => {
      if (frame) return;
      frame = window.requestAnimationFrame(() => {
        root.style.setProperty("--cursor-x", `${event.clientX}px`);
        root.style.setProperty("--cursor-y", `${event.clientY}px`);
        frame = null;
      });
    };

    window.addEventListener("mousemove", onMouseMove, { passive: true });

    return () => {
      window.removeEventListener("mousemove", onMouseMove);
      if (frame) window.cancelAnimationFrame(frame);
    };
  }, []);

  useEffect(() => {
    const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduceMotion) return;
    document.body.classList.add("motion-safe-reveal");

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("is-visible");
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.01, rootMargin: "0px 0px -8% 0px" }
    );

    const targets = document.querySelectorAll("main section, .section-surface");
    targets.forEach((element) => {
      element.classList.add("reveal-on-scroll");
      if (!element.classList.contains("is-visible")) {
        observer.observe(element);
      }
    });

    return () => observer.disconnect();
  }, [location.pathname]);

  useEffect(() => {
    if (normalizePath(location.pathname) !== "/empresas") return;

    const frame = window.requestAnimationFrame(() => {
      scrollToTop("auto");
    });

    return () => window.cancelAnimationFrame(frame);
  }, [location.pathname]);

  return (
    <div className="relative flex flex-col min-h-screen hero-gradient_thirdary">
      <SeoManager />
      <div className="cursor-glow" aria-hidden="true" />
      <div className="relative z-10 flex flex-col min-h-screen">
        <Navbar />
        <main className="flex-grow w-full max-w-screen-2xl mx-auto px-3 sm:px-5 md:px-8 lg:px-10 py-4">
          {children}
        </main>
        <Footer />
        <FloatingWhatsApp />
      </div>
    </div>
  );
}

function LandingSectionsPage() {
  const location = useLocation();
  const didMountRef = useRef(false);

  useEffect(() => {
    const behavior = didMountRef.current ? "smooth" : "auto";
    const frame = window.requestAnimationFrame(() => {
      scrollToPath(location.pathname, behavior);
      didMountRef.current = true;
    });

    return () => window.cancelAnimationFrame(frame);
  }, [location.pathname]);

  return (
    <MainLayout>
      <div className="space-y-10 md:space-y-12">
        <Home />
        <Sueros />
        <Tratamientos />
        <Nosotros />
        <Contacto />
        <Reservar />
      </div>
    </MainLayout>
  );
}

export default function App() {
  return (
    <Routes>
      <Route
        path="/sueros/:slug"
        element={
          <MainLayout>
            <SueroDetalle />
          </MainLayout>
        }
      />
      <Route
        path="/tratamientos/celulas-madre"
        element={
          <MainLayout>
            <CelulasMadre />
          </MainLayout>
        }
      />
      <Route
        path="/tratamientos/:slug"
        element={
          <MainLayout>
            <TratamientoDetalle />
          </MainLayout>
        }
      />
      <Route
        path="/empresas"
        element={
          <MainLayout>
            <Empresas />
          </MainLayout>
        }
      />
      <Route path="*" element={<LandingSectionsPage />} />
    </Routes>
  );
}
